import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { z } from 'zod';
import { RootState } from '../reducers';
import { addTagToUserThunk } from '../features/settings/settingsSlice';
import { addTeamTagThunk } from '../features/team/teamSlice';
import { addFileTag } from '../features/library/librarySlice';

// Define the base schemas
export const userTagSchema = z.object({
  onboardingStep: z.string(),
  'chrome-extension-clicked': z.enum(['true', 'false']),
  'changelog-seen-version': z.string(),
  'video-left-column-width': z.string().regex(/^\d+$/), // numeric string
  'library-view-type': z.enum(['grid', 'list']),
  'chapters-timestamps-visible': z.enum(['true', 'false']),
  'chapters-expanded': z.enum(['true', 'false']),
  'video-chapters-visible': z.enum(['true', 'false']),
  'video-captions-visible': z.enum(['true', 'false']),
  'notepad-visible': z.enum(['true', 'false']),
  'multi-ask-ai-last-output': z.string(),
  'library-sort-by': z.string(),
  'library-sort-order': z.enum(['asc', 'desc']),
  assistant_last_results: z.string(),
  assistant_last_answer: z.string(),
  viewMode: z.string(),
  sortBy: z.string(),
  sortOrder: z.enum(['asc', 'desc']),
  educationalDiscount: z.enum(['true', 'false']),
  userGoal: z.enum(['personal', 'business', 'education']),
  folder_icons: z.enum(['true', 'false']),
  calendarOnboardingSource: z.string(),
  chromeExtensionInstalled: z.enum(['true', 'false']),
  mobileAppInstalled: z.enum(['true', 'false']),
  calendarConnected: z.enum(['true', 'false']),
  userImportMethods: z.string(), // comma-separated list of import methods
  'video-analyzer-tooltip-dismissed': z.enum(['true', 'false']),
  CHAPTERS_SCREENSHOTS_VISIBLE_KEY: z.enum(['true', 'false']),
  darkMode: z.enum(['true', 'false']),
  'read-notifications-history': z.string(), // JSON string of read notification IDs and timestamps
  feature_history_btn_top: z.enum(['true', 'false']).optional(),
  'media-actions-experience': z.enum(['classic', 'new']).optional(),
  'sidebar-collapsed': z.enum(['true', 'false']).optional(),
  'sidebar-width': z.string().regex(/^\d+$/).optional(), // numeric string for sidebar width in pixels
  'folder-order': z.string().optional(), // JSON array of folder IDs in custom order
  'media-actions-order': z.string().optional(), // JSON array of media action keys in custom order
  'entity-ask-ai-history': z.string().optional(), // JSON object of conversation histories keyed by entityType-entityName
  'multi-ask-ai-history': z.string().optional(), // JSON array of multi-ask-ai conversation histories
  'files-on-sidebar': z.enum(['true', 'false']).optional(),
});

export const teamTagSchema = z.object({
  team_logo_url: z.string().optional(),
  team_model_quality_override: z.string().optional(),
  transcribing_old_files: z.enum(['true', 'false']).optional(),
  'rag-index-v1': z.string().optional(),
  contact_support_ai_enabled: z.enum(['true', 'false']).optional(),
});

// Derive TypeScript types from Zod schemas
export type UserTagKey = keyof z.infer<typeof userTagSchema>;
export type TeamTagKey = keyof z.infer<typeof teamTagSchema>;
export type FileTagKey = string; // File tags are dynamic, so we keep it as string

export type UserTagValueType = z.infer<typeof userTagSchema>;
export type TeamTagValueType = z.infer<typeof teamTagSchema>;

// Update TagValue to be a union of all possible values
export type TagValue =
  | UserTagValueType[UserTagKey]
  | TeamTagValueType[TeamTagKey]
  | string;

export interface Tag {
  key: string;
  value: TagValue;
}

export interface UserTags {
  [key: string]: TagValue;
}

export interface TeamTags {
  [key: string]: TagValue;
}

export interface FileTags {
  [key: string]: TagValue;
}

// Export validation functions
export const validateUserTag = (key: string, value: string): boolean => {
  try {
    userTagSchema.shape[key as UserTagKey].parse(value);
    return true;
  } catch {
    return false;
  }
};

export const validateTeamTag = (key: string, value: string): boolean => {
  try {
    teamTagSchema.shape[key as TeamTagKey].parse(value);
    return true;
  } catch {
    return false;
  }
};

export const useUserTags = () => {
  const dispatch = useDispatch();
  const tags = useSelector((state: RootState) => state.settings.user.tags);

  const getTag = useCallback(
    (key: UserTagKey): TagValue | null => {
      return tags[key] || null;
    },
    [tags],
  );

  const setTag = useCallback(
    ({ key, value }: { key: UserTagKey; value: TagValue }) => {
      if (tags[key] !== value) {
        dispatch(addTagToUserThunk({ key, value: value as string }));
      }
    },
    [dispatch, tags],
  );

  return { getTag, setTag };
};

export const useTeamTags = () => {
  const dispatch = useDispatch();
  const selectedTeam = useSelector(
    (state: RootState) => state.team.selectedTeam,
  );

  const getTeamTag = useCallback(
    (key: TeamTagKey): TagValue | null => {
      return selectedTeam?.tags?.[key] || null;
    },
    [selectedTeam],
  );

  const setTeamTag = useCallback(
    ({ key, value }: { key: TeamTagKey; value: TagValue }) => {
      if (selectedTeam && selectedTeam.tags?.[key] !== value) {
        dispatch(
          addTeamTagThunk({
            teamId: selectedTeam.teamId,
            tag: { key, value: value as string },
          }),
        );
      }
    },
    [dispatch, selectedTeam],
  );

  return { getTeamTag, setTeamTag };
};

export const useFileTags = (fileId: string) => {
  const dispatch = useDispatch();
  const selectedFiles = useSelector(
    (state: RootState) => state.library.selectedFiles,
  );
  const file = selectedFiles[fileId];

  const getFileTag = useCallback(
    (key: FileTagKey): TagValue | null => {
      return file?.tags?.[key] || null;
    },
    [file],
  );

  const setFileTag = useCallback(
    ({ key, value }: { key: FileTagKey; value: TagValue }) => {
      if (file && file.tags?.[key] !== value) {
        dispatch(addFileTag({ fileId, tag: { key, value: value as string } }));
      }
    },
    [dispatch, file, fileId],
  );

  return { getFileTag, setFileTag };
};

export default useUserTags;

// Helper function to get read notification history
export const useReadNotificationHistory = () => {
  const { getTag } = useUserTags();

  const getReadNotificationHistory = (): Array<{
    id: string;
    type: string;
    title: string;
    readAt: number;
  }> => {
    try {
      const historyString = getTag('read-notifications-history');
      if (historyString) {
        return JSON.parse(historyString as string);
      }
      return [];
    } catch (error) {
      console.error('Failed to parse read notification history:', error);
      return [];
    }
  };

  return { getReadNotificationHistory };
};
